import React, { useEffect, useMemo, useState } from 'react';
import {
  CardContents,
  TitleContainer,
  CardTitle,
  CancelButton,
  FormError,
  InputContainer,
  AmountInput,
  PrimaryButton,
  ButtonContainer,
  Selector,
  InputBox,
} from 'common/components/cardStyles';
import Icon from '@mdi/react';
import { mdiClose } from '@mdi/js';
import { useProducerWithThunks } from 'common/hooks/useProducer';

import { defaultAPIRequestState } from 'api';
import { CustomerSummary } from 'common/state/customers/actions';
import {
  adjustAccountRecipe,
  transferAdjustAccountRecipe,
} from 'common/state/accounts/recipes';
import {
  AdjustAccountBodyData,
  AdjustmentTypes,
  doAdjustAccount,
  doTransferAdjustAccount,
  TransferAdjustAccountBodyData,
} from 'common/state/accounts/thunkActions';
import { ProviderSummary } from 'common/state/provider/actions';
import { OptionType } from '@mollybet/ui/dist/components/Select';
import { CurrencyCodes } from 'common/configs/currency';

export interface AdjustmentModalProps {
  customerSummaries: CustomerSummary[];
  providerSummaries: ProviderSummary[];
  setShouldRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  closeModal: () => void;
  initialData?: {
    sourceClientCode?: string;
    sourceAmount?: string;
    destinationClientCode?: string;
    destinationAmount?: string;
    rate?: string;
    description?: string;
    notes?: string;
    date?: string;
    type?: AdjustmentTypes;
  };
}

interface SelectedSummary {
  ccy_code: CurrencyCodes;
  username: string;
}

const CURRENCY_CONVERT_TYPE = 'CURRENCY_CONVERT';
const TYPE_OPTIONS = [
  { value: AdjustmentTypes.ADJUSTMENT, label: 'Adjustment' },
  { value: AdjustmentTypes.ADJUSTING_TRANSFER, label: 'Adjusting Transfer' },
  { value: CURRENCY_CONVERT_TYPE, label: 'Currency Convert' },
];

const ADJUST_TYPES = new Set([AdjustmentTypes.ADJUSTMENT]);
const TRANSFER_TYPES = new Set([AdjustmentTypes.ADJUSTING_TRANSFER]);
const CURRENCY_CONVERT_TYPES = new Set([CURRENCY_CONVERT_TYPE]);

export function AdjustmentModal({
  customerSummaries,
  providerSummaries,
  setShouldRefresh,
  closeModal,
  initialData,
}: AdjustmentModalProps) {
  const [sourceClientCode, setSourceClientCode] = useState(
    initialData?.sourceClientCode || ''
  );
  const [destinationClientCode, setDestinationClientCode] = useState<
    string | undefined
  >(initialData?.destinationClientCode || '');
  const [sourceAmount, setSourceAmount] = useState(
    initialData?.sourceAmount || ''
  );
  const [destinationAmount, setDestinationAmount] = useState(
    initialData?.destinationAmount || ''
  );
  const [rate, setRate] = useState(initialData?.rate || '');
  const [description, setDescription] = useState(
    initialData?.description || ''
  );
  const [notes, setNotes] = useState(initialData?.notes || '');
  const [date, setDate] = useState(
    initialData?.date || new Date().toISOString().replace('Z', '')
  );
  const [type, setType] = useState<AdjustmentTypes>(
    initialData?.type || AdjustmentTypes.ADJUSTMENT
  );

  const [adjustState, adjustDispatch] = useProducerWithThunks(
    adjustAccountRecipe,
    defaultAPIRequestState
  );
  const [transferAdjustState, transferAdjustDispatch] = useProducerWithThunks(
    transferAdjustAccountRecipe,
    defaultAPIRequestState
  );

  const isAdjustType = useMemo(() => ADJUST_TYPES.has(type), [type]);
  const isTransferType = useMemo(() => TRANSFER_TYPES.has(type), [type]);
  const isCurrencyConvertType = useMemo(
    () => CURRENCY_CONVERT_TYPES.has(type),
    [type]
  );

  const submit: React.FormEventHandler = React.useCallback(
    (e) => {
      e.preventDefault();
      if (isAdjustType) {
        const data: AdjustAccountBodyData = {
          type,
          amount: sourceAmount,
          description,
          post_date: date,
        };
        if (notes) data.notes = notes;
        adjustDispatch(doAdjustAccount(sourceClientCode, data));
      }
      if (isCurrencyConvertType) {
        const data: Partial<AdjustAccountBodyData> = {
          type: AdjustmentTypes.ADJUSTMENT,
          description,
          post_date: date,
        };
        if (notes) data.notes = notes;
        adjustDispatch(
          doAdjustAccount(sourceClientCode, {
            ...data,
            amount: `-${sourceAmount}`,
          } as AdjustAccountBodyData)
        );
        adjustDispatch(
          doAdjustAccount(destinationClientCode, {
            ...data,
            amount: destinationAmount,
          } as AdjustAccountBodyData)
        );
      }
      if (isTransferType) {
        const data: TransferAdjustAccountBodyData = {
          source: sourceClientCode,
          target: destinationClientCode,
          type,
          amount: sourceAmount,
          description,
          post_date: date,
        };
        if (notes) data.notes = notes;
        transferAdjustDispatch(doTransferAdjustAccount(data));
      }
    },
    [
      adjustDispatch,
      transferAdjustDispatch,
      sourceClientCode,
      destinationClientCode,
      sourceAmount,
      destinationAmount,
      date,
      notes,
      isAdjustType,
      isCurrencyConvertType,
      isTransferType,
      description,
      type,
    ]
  );

  // Close the modal if the request succeeded
  const success = adjustState.success || transferAdjustState.success;
  const error = adjustState.error || transferAdjustState.error;

  useEffect(() => {
    if (success) {
      closeModal();
      setShouldRefresh(true);
    }
  }, [closeModal, setShouldRefresh, success]);

  useEffect(() => {
    setSourceAmount(initialData?.sourceAmount || '');
    setDestinationAmount(initialData?.destinationAmount || '');
    setRate(initialData?.rate || '');
    setDescription(initialData?.description || '');
    setNotes(initialData?.notes || '');
    setDate(initialData?.date || new Date().toISOString().replace('Z', ''));
    setType(initialData?.type || AdjustmentTypes.ADJUSTMENT);
  }, [
    initialData,
    setSourceAmount,
    setDestinationAmount,
    setRate,
    setDescription,
    setNotes,
    setDate,
    setType,
  ]);

  const sourceSummary = useMemo((): SelectedSummary | null => {
    if (!sourceClientCode) return null;
    if (sourceClientCode.startsWith('provider')) {
      const [, id, , ccy_code] = sourceClientCode.split('.');
      const _id = parseInt(id);
      const _ccy_code = ccy_code as CurrencyCodes;
      return providerSummaries?.find(
        (summary) => summary.id === _id && summary.ccy_code === _ccy_code
      );
    }
    const summary = customerSummaries?.find(
      (summary) => summary.client_account_code === sourceClientCode
    );
    return summary
      ? {
          username: summary.customer_username,
          ccy_code: summary.wallet_currency as CurrencyCodes,
        }
      : null;
  }, [sourceClientCode, customerSummaries, providerSummaries]);

  const destinationSummary = useMemo((): SelectedSummary | null => {
    if (!destinationClientCode) return null;
    if (destinationClientCode.startsWith('provider')) {
      const [, id, , ccy_code] = destinationClientCode.split('.');
      const _id = parseInt(id);
      const _ccy_code = ccy_code as CurrencyCodes;
      return providerSummaries?.find(
        (summary) => summary.id === _id && summary.ccy_code === _ccy_code
      );
    }
    const summary = customerSummaries?.find(
      (summary) => summary.client_account_code === destinationClientCode
    );
    return summary
      ? {
          username: summary.customer_username,
          ccy_code: summary.wallet_currency as CurrencyCodes,
        }
      : null;
  }, [destinationClientCode, customerSummaries, providerSummaries]);

  const sourceAccountOptions = useMemo(() => {
    const options: OptionType<string, string>[] = [];

    customerSummaries?.forEach((summary) =>
      options.push({
        value: summary.client_account_code,
        label: `${summary.customer_name} (${summary.wallet_currency})`,
      })
    );

    providerSummaries?.forEach((summary) => {
      options.push({
        value: `provider.${summary.id}.provider_adjustment.${summary.ccy_code}`,
        label: `${summary.username} (${summary.ccy_code})`,
      });
      options.push({
        value: `provider.${summary.id}.totus_bookie_deposits.${summary.ccy_code}`,
        label: `${summary.username} Totus Deposits (${summary.ccy_code})`,
      });
      options.push({
        value: `provider.${summary.id}.customer_bookie_deposits.${summary.ccy_code}`,
        label: `${summary.username} Customer Deposits (${summary.ccy_code})`,
      });
      options.push({
        value: `provider.${summary.id}.pl_settled.${summary.ccy_code}`,
        label: `${summary.username} Profit/Loss Settled (${summary.ccy_code})`,
      });
      options.push({
        value: `provider.${summary.id}.singbet_dividend.${summary.ccy_code}`,
        label: `${summary.username} SingBet Dividend (${summary.ccy_code})`,
      });
      options.push({
        value: `provider.${summary.id}.commission_settled.${summary.ccy_code}`,
        label: `${summary.username} Commission Settled (${summary.ccy_code})`,
      });
    });

    return options;
  }, [customerSummaries, providerSummaries]);

  const selectedSourceAccount = useMemo(
    () =>
      sourceAccountOptions.find((option) => option.value === sourceClientCode),
    [sourceClientCode, sourceAccountOptions]
  );

  const destinationAccountOptions = useMemo(() => {
    const options: OptionType<string, string>[] = [];

    customerSummaries
      ?.filter(
        (summary) =>
          !sourceSummary ||
          isCurrencyConvertType ||
          (summary.wallet_currency === sourceSummary.ccy_code &&
            summary.customer_username !== sourceSummary.username)
      )
      .forEach((summary) =>
        options.push({
          value: summary.client_account_code,
          label: `${summary.customer_name} (${summary.wallet_currency})`,
        })
      );

    providerSummaries
      ?.filter(
        (summary) =>
          !sourceSummary ||
          isCurrencyConvertType ||
          (summary.ccy_code === sourceSummary.ccy_code &&
            summary.username !== sourceSummary.username)
      )
      .forEach((summary) => {
        options.push({
          value: `provider.${summary.id}.provider_adjustment.${summary.ccy_code}`,
          label: `${summary.username} (${summary.ccy_code})`,
        });
        options.push({
          value: `provider.${summary.id}.totus_bookie_deposits.${summary.ccy_code}`,
          label: `${summary.username} Totus Deposits (${summary.ccy_code})`,
        });
        options.push({
          value: `provider.${summary.id}.customer_bookie_deposits.${summary.ccy_code}`,
          label: `${summary.username} Customer Deposits (${summary.ccy_code})`,
        });
        options.push({
          value: `provider.${summary.id}.pl_settled.${summary.ccy_code}`,
          label: `${summary.username} Profit/Loss Settled (${summary.ccy_code})`,
        });
        options.push({
          value: `provider.${summary.id}.singbet_dividend.${summary.ccy_code}`,
          label: `${summary.username} SingBet Dividend (${summary.ccy_code})`,
        });
        options.push({
          value: `provider.${summary.id}.commission_settled.${summary.ccy_code}`,
          label: `${summary.username} Commission Settled (${summary.ccy_code})`,
        });
      });

    return options;
  }, [
    customerSummaries,
    providerSummaries,
    sourceSummary,
    isCurrencyConvertType,
  ]);

  const selectedDestinationAccount = useMemo(
    () =>
      destinationAccountOptions.find(
        (option) => option.value === destinationClientCode
      ),
    [destinationClientCode, destinationAccountOptions]
  );

  const selectedType = useMemo(
    () => TYPE_OPTIONS.find((option) => option.value === type),
    [type]
  );

  return (
    <form onSubmit={submit}>
      <CardContents>
        <TitleContainer>
          <CardTitle>Adjust Account</CardTitle>
          <CancelButton type="button" onClick={closeModal}>
            <Icon size={0.8} path={mdiClose} />
          </CancelButton>
        </TitleContainer>
        <TitleContainer>
          {error && <FormError>{error}</FormError>}
        </TitleContainer>
        <InputContainer>
          <Selector
            label="Type *"
            shrinked
            options={TYPE_OPTIONS}
            value={selectedType}
            onChange={(option) => setType(option.value)}
          />
        </InputContainer>
        <InputContainer>
          <Selector
            label={isTransferType ? 'Source Account *' : 'Account *'}
            shrinked
            options={sourceAccountOptions}
            value={selectedSourceAccount}
            help={`Xact code: ${sourceClientCode}`}
            onChange={(option) => setSourceClientCode(option.value)}
          />
          {isTransferType || isCurrencyConvertType ? (
            <Selector
              label={'Destination Account *'}
              shrinked
              options={destinationAccountOptions}
              value={selectedDestinationAccount}
              help={
                destinationClientCode && `Xact code: ${destinationClientCode}`
              }
              onChange={(option) => setDestinationClientCode(option.value)}
            />
          ) : null}
        </InputContainer>
        <InputContainer>
          <AmountInput
            currency={sourceSummary?.ccy_code || 'NONE'}
            shrinked
            label={isCurrencyConvertType ? 'Source Amount *' : 'Amount *'}
            value={sourceAmount}
            onChange={(e) => {
              let amount = e.target.value.replaceAll(',', '');
              if (isCurrencyConvertType) amount = amount.replaceAll('-', '');
              setSourceAmount(amount);

              if (isCurrencyConvertType && rate) {
                setDestinationAmount(
                  (
                    parseFloat(
                      e.target.value
                        ?.replaceAll(',', '')
                        ?.replaceAll('-', '') || '0'
                    ) * parseFloat(rate)
                  ).toString()
                );
              }
            }}
          />
          {isCurrencyConvertType && (
            <AmountInput
              currency="NONE"
              shrinked
              label="Rate *"
              value={rate}
              onChange={(e) => {
                setRate(e.target.value.replaceAll(',', '').replaceAll('-', ''));
                setDestinationAmount(
                  (
                    parseFloat(
                      e.target.value
                        ?.replaceAll(',', '')
                        ?.replaceAll('-', '') || '0'
                    ) * parseFloat(sourceAmount || '0')
                  ).toString()
                );
              }}
            />
          )}
        </InputContainer>
        {isCurrencyConvertType && (
          <InputContainer>
            <AmountInput
              currency={destinationSummary?.ccy_code || 'NONE'}
              shrinked
              label="Destination Amount *"
              value={destinationAmount}
              onChange={(e) => {
                setDestinationAmount(
                  e.target.value.replaceAll(',', '').replaceAll('-', '')
                );
                setRate(
                  (
                    parseFloat(
                      e.target.value
                        ?.replaceAll(',', '')
                        ?.replaceAll('-', '') || '0'
                    ) / parseFloat(sourceAmount || '0')
                  ).toString()
                );
              }}
            />
            <InputBox style={{ opacity: 0 }} />
          </InputContainer>
        )}
        <InputContainer>
          <InputBox
            shrinked
            label="Description *"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </InputContainer>
        <InputContainer>
          <InputBox
            shrinked
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <InputBox
            type="datetime-local"
            shrinked
            label="Date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </InputContainer>
        <ButtonContainer>
          <PrimaryButton
            type="submit"
            disabled={
              !sourceAmount ||
              !date ||
              !description ||
              !type ||
              (isTransferType && !destinationClientCode) ||
              (isCurrencyConvertType &&
                (!destinationClientCode || !destinationAmount))
            }
          >
            Submit
          </PrimaryButton>
        </ButtonContainer>
      </CardContents>
    </form>
  );
}
